import type { ApolloError, ServerError } from '@apollo/client';
import { Callout } from '@radix-ui/themes';
import type { GraphQLFormattedError } from 'graphql';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { i18n } from '../../i18n';

export interface GraphQLErrorWithCodeAndReason extends GraphQLFormattedError {
  code?: string;
  reason?: string;
}

const SignInUrl = () => {
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  return `/signin?redirect=${returnTo}`;
};

const accessTokenMissingOrExpired = (error: ApolloError) => {
  const { networkError } = error;
  if (networkError && (networkError as ServerError).statusCode === 403) return true;

  const { graphQLErrors } = error as unknown as { graphQLErrors: readonly GraphQLErrorWithCodeAndReason[] };
  if (graphQLErrors && graphQLErrors.length > 0)
    for (const item of graphQLErrors)
      if (
        item.code === 'UNAUTHORIZED' &&
        (item.reason === 'access-token-missing' || item.reason === 'access-token-expired')
      )
        return true;

  return false;
};

export const Error = (error?: ApolloError, defaultError?: () => ReactNode) => {
  const navigate = useNavigate();

  if (!error) return <></>;
  if (accessTokenMissingOrExpired(error)) {
    toast.warning(i18n.t('error.sessionExpired.toast'), {
      action: {
        label: i18n.t('error.sessionExpired.signInButton'),
        onClick: () => navigate(SignInUrl(), { replace: true }),
      },
    });
    return <></>;
  }
  if (defaultError) return defaultError();

  return (
    <Callout.Root color="red">
      <Callout.Text className="mb-0">{error && <pre>{JSON.stringify(error, null, 2)}</pre>}</Callout.Text>
    </Callout.Root>
  );
};
